import React, { useState, useEffect } from 'react';
import { contractsAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, FileSignature, Trash2, Pencil, Loader2, Search, Eye, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SignatureDialog from '../components/SignatureDialog';

const STATUS_STYLES = { draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700', signed: 'bg-green-100 text-green-700' };

export default function Contracts() {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const emptyForm = { title: '', content: '', project_id: '', client_name: '', status: 'draft' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    Promise.all([contractsAPI.list(), projectsAPI.list(), contractsAPI.templates()])
      .then(([c, p, t]) => { setContracts(c.data); setProjects(p.data); setTemplates(t.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editing) { await contractsAPI.update(editing.contract_id, form); }
      else { await contractsAPI.create(form); }
      const res = await contractsAPI.list();
      setContracts(res.data);
      setDialogOpen(false); setEditing(null); setForm(emptyForm);
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => { try { await contractsAPI.delete(id); setContracts(c => c.filter(x => x.contract_id !== id)); } catch (e) { console.error(e); } };

  const loadTemplate = (idx) => { setForm({ ...form, content: templates[idx]?.content || '', title: templates[idx]?.name || form.title }); };

  const filtered = contracts.filter(c => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return c.title?.toLowerCase().includes(t) || c.client_name?.toLowerCase().includes(t) || c.project_name?.toLowerCase().includes(t);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="contracts-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3"><FileSignature className="h-7 w-7 text-muted-foreground" />Contracts</h1>
            <p className="text-sm text-muted-foreground mt-1">Design agreements and scope of work</p>
          </div>
          {canEdit && <Button onClick={() => { setForm(emptyForm); setEditing(null); setDialogOpen(true); }} data-testid="create-contract-btn"><Plus className="h-4 w-4 mr-2" />New Contract</Button>}
        </div>

        <div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search contracts..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="contract-search" /></div>

        {loading ? <div className="animate-pulse space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-muted rounded" />)}</div> : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><FileSignature className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No contracts found</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(c => (
              <Card key={c.contract_id} data-testid={`contract-${c.contract_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm">{c.title}</h4>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>{c.status}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                        {c.client_name && <span>Client: {c.client_name}</span>}
                        {c.project_name && <span>Project: {c.project_name}</span>}
                        <span>{new Date(c.created_at).toLocaleDateString()}</span>
                        {c.signed_at && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Signed {new Date(c.signed_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelected(c); setViewOpen(true); }}><Eye className="h-3.5 w-3.5" /></Button>
                      {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(c); setForm({ title: c.title, content: c.content || '', project_id: c.project_id || '', client_name: c.client_name || '', status: c.status }); setDialogOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>}
                      {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(c.contract_id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{selected?.title}</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-4 py-2">
                <div className="prose prose-sm max-w-none"><pre className="whitespace-pre-wrap text-sm font-sans bg-muted/30 p-4 rounded">{selected.content}</pre></div>
                {selected.signature ? (
                  <div className="border-t pt-3"><p className="text-sm font-medium mb-2">Signed</p><img src={selected.signature} alt="Signature" className="h-16 border rounded bg-white" /><p className="text-xs text-muted-foreground mt-1">By {selected.signed_by_name} on {new Date(selected.signed_at).toLocaleString()}</p></div>
                ) : (
                  <div className="border-t pt-3 flex justify-between items-center"><p className="text-xs text-muted-foreground">Not yet signed</p><Button size="sm" onClick={() => setSignOpen(true)} data-testid="sign-contract-btn">Sign Contract</Button></div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Signature */}
        {selected && <SignatureDialog open={signOpen} onOpenChange={setSignOpen} documentType="contract" documentId={selected.contract_id} onSigned={async () => { const res = await contractsAPI.list(); setContracts(res.data); const upd = res.data.find(c => c.contract_id === selected.contract_id); if (upd) setSelected(upd); }} />}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit Contract' : 'New Contract'}</DialogTitle><DialogDescription>Create a design agreement or scope of work</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Design Agreement" data-testid="contract-title-input" /></div>
                  <div className="space-y-2"><Label>Client Name</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Project</Label>
                    <Select value={form.project_id || 'none'} onValueChange={v => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Project</SelectItem>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Template</Label>
                    <Select value="none" onValueChange={v => v !== 'none' && loadTemplate(parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Load template..." /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Choose template</SelectItem>{templates.map((t, i) => <SelectItem key={i} value={String(i)}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Content</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={12} placeholder="Contract terms..." className="font-mono text-sm" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="save-contract-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? 'Update' : 'Create Contract'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
